

































































































































































































/* General */
import Vue from 'vue'
import { isEqual } from 'lodash'

/* Mixins */

/* Components */
import BepOptionsTab from './bep-options-tab.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import AssigneeAssistant from '@/helpers/assignee-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IItemForSelection,
  IPersonBase,
  IPriorityLevelForSelection,
  IProjectForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'
import ConversationsAPI from '@/api/modules/convos.api'
import {
  IConvosInputBulkEditAssignee,
  IConvosInputBulkEditDueDate,
  IConvosInputBulkEditJobAppointee,
  IConvosInputBulkEditPriorityLevel,
  IConvosInputBulkEditStatus,
  IConvosInputBulkEditTags,
  ITasksInputBulkEditAssignee,
  ITasksInputBulkEditDueDate,
  ITasksInputBulkEditJobAppointee,
  ITasksInputBulkEditPriorityLevel,
  ITasksInputBulkEditStatus,
  ITasksInputBulkEditTags,
} from '@/api/types/request.types'

export default Vue.extend({
  name: 'BulkEditPanel',
  components: { BepOptionsTab },
  props: {
    selectedIds: {
      type: Array as () => number[],
      default: (): number[] => [],
    },
    selectedEntityIds: {
      type: Array as () => number[],
      default: (): number[] => [],
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    isSpamFilterSelected: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      TextAssistant,
      isLoadingOptions: true,

      isSubmitting: false,
      isSpamUpdating: false,
      isWatchUpdating: false,
      isDeleting: false,
      isTagsUpdating: false,

      entityOptions: [] as IProjectForSelection[],
      priorityOptions: [] as IPriorityLevelForSelection[],
      assigneeOptions: [] as IPersonBase[],
      tagOptions: [] as string[],
      statusOptions: [] as IStatusForSelection[],
      jobOptions: [] as IItemForSelection[],
      allSelectedEntitiesUseAssignees: false,

      selectedPriorityID: null,
      // Used for both assignee and job(s) dropdowns. Since this is just used to temporarily show
      // selected state of dropdown while the bulk edit is being made, its fine to reuse it
      // across multiple dropdowns (it just means that person is selected within each dropdown
      // during the duration of the update.)
      selectedPersonID: null,
      selectedStatusName: null,
      selectedJobName: null,
      selectedDueDate: null,
      selectedTags: [] as string[],
      removeSelectedTags: false,

      comment: null,
    }
  },

  computed: {
    hasStatusOptions(): boolean {
      return this.statusOptions !== null && this.statusOptions.length > 0
    },
    hasJobOptions(): boolean {
      return this.jobOptions !== null && this.jobOptions.length > 0
    },
    curatedAssigneeOptions(): IPersonBase[] {
      return AssigneeAssistant.getFullAssigneeOptions(
        this.assigneeOptions,
        this.$permissions,
      )
    },
    showMergeButton(): boolean {
      return (
        this.isConvoRelated &&
        this.selectedEntityIds.length === 1 &&
        this.selectedIds.length > 1 &&
        this.selectedIds.length <= 10
      )
    },
    showMoveButton(): boolean {
      return this.selectedEntityIds.length === 1
    },
    showSpamButton(): boolean {
      return this.isConvoRelated && !this.isSpamFilterSelected
    },
    showUnSpamButton(): boolean {
      return this.isConvoRelated && this.isSpamFilterSelected
    },
    isConvoRelated(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    selectedRowsCount(): number {
      return this.selectedIds.length
    },
    hasSelectedTags(): boolean {
      return this.selectedTags.length > 0
    },
    tagsLabel(): string {
      return this.selectedTags.length === 1 ? 'tag' : 'tags'
    },
    itemCountLabel(): string {
      if (this.selectedRowsCount === 1) {
        return `One ${TextAssistant.itemSingularName(
          this.entityType,
        ).toLowerCase()}`
      } else {
        return `${this.selectedRowsCount} ${TextAssistant.itemPluralName(
          this.entityType,
        ).toLowerCase()}`
      }
    },
  },

  watch: {
    selectedEntityIds: {
      immediate: true,
      async handler(newVal, oldVal) {
        if (!isEqual(oldVal, newVal)) {
          await this.fetchOptions()
        }
      },
    },
    selectedDueDate: {
      handler(val) {
        // Null (or 'clear due date') handling explicitly using removeDueDate()
        if (val !== null) {
          this.updateDueDate()
        }
      },
    },
  },

  methods: {
    handleSuccess(successMessage: string) {
      this.$messaging.showSuccess(successMessage)
      this.$emit('edit-completed')
    },
    async handleSpamOptionSelected() {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true
      this.isSpamUpdating = true
      try {
        const response = await ConversationsAPI.put_BulkEdit_Spam(
          this.selectedIds,
        )
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isSpamUpdating = false
      }
    },
    async handleUnSpamOptionSelected() {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.isSpamUpdating = true

      try {
        const response = await ConversationsAPI.put_BulkEdit_UnSpam(
          this.selectedIds,
        )
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isSpamUpdating = false
      }
    },
    async handleWatchOptionSelected() {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.isWatchUpdating = true

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Watch(this.selectedIds)
        } else {
          response = await TasksAPI.put_BulkEdit_Watch(this.selectedIds)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isWatchUpdating = false
      }
    },
    async handleDeleteOptionSelected() {
      if (this.isSubmitting) {
        return
      }

      await this.$confirm(
        `Are you sure you want to delete ${this.itemCountLabel.toLowerCase()}?<br>There is no undo.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Delete ${this.itemCountLabel.toLowerCase()}`,
        },
      )
        .then(async () => {
          let response

          this.isSubmitting = true
          this.isDeleting = true
          if (this.isConvoRelated) {
            response = await ConversationsAPI.put_BulkEdit_Delete(
              this.selectedIds,
            )
          } else {
            response = await TasksAPI.put_BulkEdit_Delete(this.selectedIds)
          }
          this.handleSuccess(response.message)
          this.isSubmitting = false
          this.isDeleting = false
        })
        .catch(error => {
          this.$error.emit(error, true)
          this.isSubmitting = false
          this.isDeleting = false
        })
    },

    async handleAssigneeSelected(id: number) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedPersonID = id

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Assignee({
            assigneeID: id,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditAssignee)
        } else {
          response = await TasksAPI.put_BulkEdit_Assignee({
            assigneeID: id,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditAssignee)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedPersonID = null
      }
    },

    async handleJobAppointeeSelected(jobName: string, appointeeID: number) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedPersonID = appointeeID

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_JobAppointee({
            appointeeID,
            jobName,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditJobAppointee)
        } else {
          response = await TasksAPI.put_BulkEdit_JobAppointee({
            appointeeID,
            jobName,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditJobAppointee)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedPersonID = null
      }
    },

    async handlePrioritySelected(id: number) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedPriorityID = id

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_PriorityLevel({
            priorityID: id,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditPriorityLevel)
        } else {
          response = await TasksAPI.put_BulkEdit_PriorityLevel({
            priorityID: id,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditPriorityLevel)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedPriorityID = null
      }
    },
    async handleStatusSelected(name: string) {
      if (this.isSubmitting) {
        return
      }

      this.isSubmitting = true
      this.selectedStatusName = name

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Status({
            statusName: name,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditStatus)
        } else {
          response = await TasksAPI.put_BulkEdit_Status({
            statusName: name,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditStatus)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedStatusName = null
      }
    },
    async updateDueDate() {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_DueDate({
            dueDate: this.selectedDueDate,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditDueDate)
        } else {
          response = await TasksAPI.put_BulkEdit_DueDate({
            dueDate: this.selectedDueDate,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditDueDate)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.selectedDueDate = null
      }
    },
    async removeDueDate() {
      this.selectedDueDate = null
      await this.updateDueDate()
    },
    async addTags() {
      await this.updateTags(false)
    },
    async removeTags() {
      await this.updateTags(true)
    },
    async updateTags(remove: boolean) {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true
      this.isTagsUpdating = true

      try {
        let response
        if (this.isConvoRelated) {
          response = await ConversationsAPI.put_BulkEdit_Tags({
            removeTags: remove,
            listTags: this.selectedTags,
            listConversationIDs: this.selectedIds,
          } as IConvosInputBulkEditTags)
        } else {
          response = await TasksAPI.put_BulkEdit_Tags({
            removeTags: remove,
            listTags: this.selectedTags,
            listTaskIDs: this.selectedIds,
          } as ITasksInputBulkEditTags)
        }
        this.handleSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
        this.isTagsUpdating = false
        this.clearSelectedTags()
      }
    },
    clearSelectedTags() {
      this.selectedTags = []
    },
    async fetchOptions() {
      try {
        this.isLoadingOptions = true
        const response = this.isConvoRelated
          ? await ConversationsAPI.get_BulkEditOptions(
              TextAssistant.convertNumberArrayToStringList(
                this.selectedEntityIds,
              ),
            )
          : await TasksAPI.get_BulkEditOptions(
              TextAssistant.convertNumberArrayToStringList(
                this.selectedEntityIds,
              ),
            )

        this.entityOptions = response.listEntities
        this.assigneeOptions = response.listAssignees
        this.statusOptions = response.listStatuses
        this.priorityOptions = response.listPriorities
        this.tagOptions = response.listTags.map(t => t.name)
        this.jobOptions = response.listJobs
        this.allSelectedEntitiesUseAssignees =
          response.allSelectedEntitiesUseAssignees

        this.isLoadingOptions = false
      } catch (error) {
        this.$error.emit(error)
      }
    },
  },
})
